/**
 * Ward boundaries: ONE source for the dashboard, the onboarding ward lookup and the Worker (brief §0.4 "Ward
 * boundaries", screenshots/75). It is the public site's /data/wards.geojson, built daily by scripts/fetch_data.py
 * build_wards() from the City of Chicago's "Boundaries - Wards (2023-)" (p293-wvbd), simplified at ~5 m. The design
 * starter's public/geo/chicago-wards.json is the same City dataset simplified at ~25 m; on 3,000 random points in
 * Chicago it disagreed with the City's own boundaries 8 times, ours 0 times (tests/geo.test.ts pins 1,000 of them),
 * so we keep one finer file instead of two.
 *
 * The Ward map registers it with ECharts (feature property `ward` as "1"…"50"); point-in-polygon is wardAt()
 * (onboarding/wardLookup.ts), the same rule the public site and the Worker use (holes excluded).
 */
export const WARDS_URL = "/data/wards.geojson";

type Ring = [number, number][];
export interface WardsGeoJSON { type: "FeatureCollection"; features: { type: "Feature"; properties: { ward: number | string }; geometry: { type: "Polygon" | "MultiPolygon"; coordinates: Ring[] | Ring[][] } }[] }

/** For echarts.registerMap: `ward` as a string, sorted, and exactly the 50 wards (scripts/build-ward-geo.mjs's check). */
export function forECharts(gj: WardsGeoJSON): WardsGeoJSON {
  const features = gj.features.map((f) => ({ ...f, properties: { ward: String(f.properties.ward) } })).sort((a, b) => Number(a.properties.ward) - Number(b.properties.ward));
  const wards = new Set(features.map((f) => f.properties.ward));
  if (features.length !== 50 || wards.size !== 50 || ![...wards].every((w) => Number(w) >= 1 && Number(w) <= 50)) throw new Error(`Expected the 50 wards, got ${features.length}/${wards.size}`);
  return { type: "FeatureCollection", features };
}

let loading: Promise<boolean> | null = null;
let registered = false;
export const wardBoundariesReady = () => registered;

/** Fetches the boundaries once and registers them (resolves false when they can't load: the map keeps the tile grid). */
export function loadWardBoundaries(register: (gj: object) => void): Promise<boolean> {
  if (!loading) {
    loading = fetch(WARDS_URL).then((r) => (r.ok ? r.json() : null)).then((gj: WardsGeoJSON | null) => {
      if (!gj) return false;
      register(forECharts(gj));
      registered = true;
      return true;
    }).catch(() => false);
  }
  return loading;
}
